import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import useTyped from '../hooks/useTyped';
import CtaSection from '../components/CtaSection';
import logo from '../assets/logo.png'; 
import SplitCompare from '../components/SplitCompare';
import transformationGif from '../assets/transformation.gif';
import AutoTabs from '../components/AutoTabs';
import OrbitStack from '../components/OrbitStack';
import Marquee from '../components/Marquee';
import TechConstellation from '../components/TechConstellation';
import SEO from '../components/SEO';

const SERVICES = [
  { ico:'⚙️', title:'Backend Development',      desc:'Secure, scalable systems with Django, FastAPI, PostgreSQL — from new builds to codebase takeovers.', tags:['Django','FastAPI','PostgreSQL','REST APIs'] },
  { ico:'🤖', title:'AI & LLM Integration',      desc:'OpenAI, Ollama, RAG pipelines and structured extraction — AI embedded into real workflows, not demos.', tags:['OpenAI','Ollama','RAG','Embeddings'] },
  { ico:'📄', title:'AI Document Processing',    desc:'OCR + NLP pipelines for invoices, PDFs, enterprise documents with confidence scoring and review queues.', tags:['OCR','NLP','PDF','Confidence Scoring'] },
  { ico:'☁️', title:'Cloud & DevOps',            desc:'AWS EC2, RDS, Lambda, Docker — clean CI/CD, staging/production parity, and observability from day one.', tags:['AWS','Docker','Lambda','GitHub Actions'] },
  { ico:'🔄', title:'Workflow Automation',       desc:'n8n, webhooks, event-driven pipelines — eliminate hours of manual work with auditable automation layers.', tags:['n8n','Webhooks','Celery','Event-Driven'] },
  { ico:'📊', title:'Data Engineering',          desc:'Airflow DAGs, ETL pipelines, SQL optimization, and analytics backends that surface business data reliably.', tags:['Airflow','ETL','SQL','MLflow'] },
];

const WORK = [
  { n:'01', title:'AI Invoice Processing Automation',         prob:'200+ invoices manually per week — 15+ hours of error-prone data entry and bottlenecks at month-end.', flow:['PDF Upload','OCR','AI Extraction','Accounting API'], tags:['Python','OpenAI','OCR','FastAPI','AWS S3'],       impact:'85% reduction in manual processing time.' },
  { n:'02', title:'Django Knowledge Management System',       prob:'Hundreds of internal policy docs scattered across folders. Staff spent hours searching for outdated info.', flow:['User Query','Semantic Search','OpenAI LLM','Sourced Answer'], tags:['Django','PostgreSQL','OpenAI','pgvector'], impact:'Knowledge retrieval from minutes to seconds.' },
  { n:'03', title:'Solar Simulation & Optimization Backend',  prob:'Solar teams needed parallel simulation across 100+ sites — manual processes could not scale to ~100MW volume.', flow:['Site Data','Weather APIs','Simulation','File Export'], tags:['Python','AWS Lambda','DynamoDB','Redis','NASA API'], impact:'Simulation cut from days to hours. ~100MW scale.' },
  { n:'04', title:'LLM Backend API Integration',              prob:'Existing SaaS needed AI without a rewrite — previous attempts caused latency spikes and spiraling API costs.', flow:['Client App','AI Layer','Cache','OpenAI'], tags:['Python','OpenAI','Redis Cache','Structured Output'], impact:'40% API cost reduction. Latency under 800ms p95.' },
];

const CHIPS = [
  { label:'Python',     style:{ top:'7%',   left:'50%', transform:'translateX(-50%)' } },
  { label:'Django',     style:{ top:'50%',  right:'1%', transform:'translateY(-50%)' } },
  { label:'FastAPI',    style:{ bottom:'7%',left:'50%', transform:'translateX(-50%)' } },
  { label:'AWS',        style:{ top:'50%',  left:'1%',  transform:'translateY(-50%)' } },
  { label:'OpenAI',     style:{ top:'17%',  right:'10%' } },
  { label:'Docker',     style:{ bottom:'17%',right:'10%' } },
  { label:'PostgreSQL', style:{ bottom:'17%',left:'8%' } },
  { label:'Airflow',    style:{ top:'17%',  left:'10%' } },
];
const TABS = [
  { label:'Backend', title:'Backend Development',   icon:'⚙️', desc:'Django, FastAPI, PostgreSQL — production-grade APIs built to scale from day one.' },
  { label:'AI',      title:'AI & LLM Integration',  icon:'🤖', desc:'OpenAI, Ollama, RAG pipelines embedded into real business workflows — not demos.' },
  { label:'Cloud',   title:'Cloud & DevOps',         icon:'☁️', desc:'AWS, Docker, GitHub Actions — clean CI/CD with staging/prod parity and full observability.' },
  { label:'Automate',title:'Workflow Automation',    icon:'🔄', desc:'n8n, webhooks, event-driven pipelines — replace manual ops at scale reliably.' },
  { label:'Data',    title:'Data Engineering',       icon:'📊', desc:'Airflow DAGs, ETL pipelines, SQL optimization — business data at scale, reliably.' },
];

export default function Home() {
  useReveal();
  const typed = useTyped();

  useEffect(() => {
    const counters = document.querySelectorAll('[data-count]');
    counters.forEach(el => {
      const target = +el.dataset.count;
      const suffix = el.dataset.suffix || '';
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(el);
        let start = null;
        const step = ts => {
          if (!start) start = ts;
          const p = Math.min((ts - start) / 500, 1);
          const ease = p < 0.5 ? 2*p*p : -1+(4-2*p)*p;
          el.textContent = Math.round(ease * target) + suffix;
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
      observer.observe(el);
    });
  }, []);

// Add to global.css: .typed-cursor{display:inline-block;width:2px;height:.85em;background:var(--cyan);vertical-align:text-bottom;animation:bc 1s step-end infinite;margin-left:2px}

  return (
    <div className="page">
        <SEO
        title="Arhatinfo — Backend Engineering, AI Automation & Cloud Solutions"
        description="Arhatinfo builds scalable backend systems, AI-powered automation, and cloud-native applications. Django, FastAPI, OpenAI, AWS — trusted by businesses worldwide."
        keywords="backend engineering, AI automation, cloud solutions, Django development, FastAPI, Python developer, OpenAI integration, AWS, software agency, Rohit Jain, arhatinfo"
        canonical="https://arhatinfo.com"
        og={{
          title:       "Arhatinfo — Backend Engineering & AI Automation",
          description: "We build scalable backend systems, AI automation, and cloud infrastructure. Python · Django · OpenAI · AWS.",
          url:         "https://arhatinfo.com",
          image:       "https://arhatinfo.com/og-home.png",
        }}
      />
      {/* HERO */}
      <section className="hero">
        <div className="blob b1" />
        <div className="blob b2" />
        <div className="blob b3" />
        <div className="hero-inner">
          <div>
            <div className="pill">
              <span className="pdot" />
              Available for new projects · 2026
            </div>
         
            <p className="hero-sub">
              <h1>We build <span className="gr-b">{typed}<span className="typed-cursor"/></span></h1>

            </p>
            <div className="hero-btns">
              <Link to="/contact" className="btn-p">Book a Free Consultation →</Link>
              <Link to="/case-studies" className="btn-g">See Our Work</Link>
            </div>
            <div className="hero-stats">
              <div><div className="stat-num" data-count="5" data-suffix="+">5+</div><div className="stat-label">Years experience</div></div>
              <div><div className="stat-num">100MW+</div><div className="stat-label">Solar infrastructure</div></div>
              <div><div className="stat-num" data-count="6" data-suffix="">6</div><div className="stat-label">Core services</div></div>
              <div><div className="stat-num">24hr</div><div className="stat-label">Response time</div></div>
            </div>
          </div>

          {/* Code Window */}
          {/* <div className="cwin">
            <div className="cbar">
              <span className="dr"/><span className="dy"/><span className="dg"/>
              <span className="cfile">invoice_pipeline.py</span>
            </div>
            <div className="cbody">
              <div><span className="cc"># AI Document Processing Pipeline</span></div>
              <div><span className="ck">from</span><span className="cfn"> arhat.core </span><span className="ck">import</span><span className="ct"> Pipeline</span></div>
              <div>&nbsp;</div>
              <div><span className="ck">async def</span><span className="cfn"> process_invoice</span><span className="cw">(pdf):</span></div>
              <div><span className="cw">{"  text = "}</span><span className="ck">await</span><span className="cfn"> ocr.extract</span><span className="cw">(pdf)</span></div>
              <div>&nbsp;</div>
              <div><span className="cw">{"  result = "}</span><span className="ck">await</span><span className="cfn"> llm.parse</span><span className="cw">(text, {"{"}</span></div>
              <div><span className="cs">{"    \"vendor\""}</span><span className="cw">{": str,  "}</span><span className="cs">{"\"amount\""}</span><span className="cw">{": float,"}</span></div>
              <div><span className="cs">{"    \"date\""}</span><span className="cw">{": "}</span><span className="ct">datetime</span><span className="cw">{", "}</span><span className="cs">{"\"confidence\""}</span><span className="cw">{": "}</span><span className="cn">0.95</span></div>
              <div><span className="cw">{"  })"}</span></div>
              <div>&nbsp;</div>
              <div><span className="ck">{"  if"}</span><span className="cw">{" result.confidence > "}</span><span className="cn">0.9</span><span className="cw">:</span></div>
              <div><span className="ck">{"    await"}</span><span className="cfn"> accounting.push</span><span className="cw">(result)</span></div>
              <div><span className="ck">{"    return"}</span><span className="cs">{" \"✓ Processed\""}</span><span className="blink"/></div>
            </div>
          </div> */}
          <TechConstellation />
        </div>
      </section>

      <hr className="ghr" />
      <Marquee />
      {/* SERVICES */}
      <section className="section">
        <div className="wrap">
          <div className="s-head rv">
            <div className="s-label">⚡ What We Build</div>
            <h2 className="s-title">Six Domains. One Engineering Partner.</h2>
            <p className="s-sub">End-to-end backend and AI engineering — from greenfield APIs to production cloud deployments.</p>
          </div>
          <div className="svc-grid rv">
            {SERVICES.map((s, i) => (
              <div key={i} className={`svc d${i+1}`}>
                <div className="svc-icon">{s.ico}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <div className="tags">{s.tags.map(t => <span key={t} className="tag">{t}</span>)}</div>
                <Link to="/services" className="svc-link">Explore →</Link>
              </div>
            ))}
          </div>
          <div className="rv d2" style={{ textAlign:'center', marginTop:'2.5rem' }}>
            <Link to="/services" className="btn-g">Explore All Services →</Link>
          </div>
        </div>
      </section>

      <hr className="ghr" />

      {/* WORK */}
      <section className="section">
        <div className="wrap">
          <div className="s-head rv">
            <div className="s-label">🏗️ Featured Work</div>
            <h2 className="s-title">Case Studies</h2>
            <p className="s-sub">Production systems built and shipped — real problems, real solutions.</p>
          </div>
          <div className="work-grid">
            {WORK.map((w, i) => (
              <div key={i} className={`wcard rv d${i+1}`}>
                <div className="wcard-num">CASE STUDY / {w.n}</div>
                <h3>{w.title}</h3>
                <div className="wlabel">Problem</div>
                <p>{w.prob}</p>
                <div className="wlabel">Pipeline</div>
                <div className="flow">
                  {w.flow.map((f, j) => (
                    <React.Fragment key={f}>
                      <span className="fstep">{f}</span>
                      {j < w.flow.length - 1 && <span className="farr">→</span>}
                    </React.Fragment>
                  ))}
                </div>
                <div className="wcard-tags">{w.tags.map(t => <span key={t} className="tag">{t}</span>)}</div>
                <div className="impact">⚡ {w.impact}</div>
              </div>
            ))}
          </div>
          <div className="rv d2" style={{ textAlign:'center', marginTop:'2.5rem' }}>
            <Link to="/case-studies" className="btn-g">View Full Case Studies →</Link>
          </div>
        </div>
      </section>
<hr className="ghr" />

{/* SPLIT COMPARE */}
<section className="section">
  <div className="wrap">
    <div className="s-head rv">
      <div className="s-label">✦ Before → After</div>
      <h2 className="s-title">
        The <span style={{ color: 'var(--blue-hi)' }}>arhatinfo</span> difference
      </h2>
      <p className="s-sub">
        Drag the handle to compare what an invoice workflow looks like
        before and after we build the AI automation layer.
      </p>
    </div>
    <div style={{ maxWidth: 720, margin: '0 auto' }} className="rv d1">
      <SplitCompare />
    </div>
  </div>

  
</section>
<hr className="ghr" />

{/* ── TRANSFORMATION ANIMATION ── */}
<section className="section section-alt">
  <div className="wrap">
    <div className="s-head rv">
      <div className="s-label">⚡ Why Arhat</div>
      <h2 className="s-title">
        Traditional vs <span className="acc">AI-Enabled</span> Delivery
      </h2>
      <p className="s-sub">
        See how the Arhat AI stack compresses months of traditional
        delivery into weeks — at every single stage.
      </p>
    </div>
    <div className="rv d1" style={{
      maxWidth: 860,
      margin: '0 auto',
    }}>
      <img
        src={transformationGif}
        alt="Traditional vs Arhat AI delivery transformation"
        style={{
          width: '100%',
          borderRadius: 'var(--r)',
          border: '1px solid var(--border-hi)',
          boxShadow: '2px 10px 30px var(--shadow)',
          display: 'block',
        }}
      />
    </div>
  </div>
</section>


<hr className="ghr" />

      {/* WHY */}
      <section className="section">
        <div className="wrap">
          <div className="why-wrap">
            <div className="rv">
              <div className="s-label">🎯 Our Approach</div>
              <h2 className="s-title">Engineering that<br />ships — and lasts.</h2>
              <p className="s-sub" style={{ marginBottom:'2.4rem' }}>
                We build architecture that survives growth, team changes, and real production load.
              </p>
              <div className="why-items">
                {[
                  { i:'🏗️', t:'Production-First Architecture', p:'Every system is designed for real-world load, maintenance cycles, and future extension — not just the demo.' },
                  { i:'⚡', t:'Fast Iteration, No Shortcuts',   p:"Battle-tested patterns let us move quickly. Clean code and speed aren't opposites." },
                  { i:'🔒', t:'Security & Reliability Built In', p:'Auth, error handling, and monitoring are part of the initial design — never bolted on after.' },
                  { i:'💬', t:'Clear Communication Always',     p:"No jargon, no ghosting. You'll always know what we're building, why, and when it ships." },
                ].map((w, i) => (
                  <div key={i} className="wi">
                    <div className="wi-ico">{w.i}</div>
                    <div><h4>{w.t}</h4><p>{w.p}</p></div>
                  </div>
                ))}
              </div>
            </div>
            {/* <OrbitStack /> */}
            <div className="cwin">
            <div className="cbar">
              <span className="dr"/><span className="dy"/><span className="dg"/>
              <span className="cfile">invoice_pipeline.py</span>
            </div>
            <div className="cbody">
              <div><span className="cc"># AI Document Processing Pipeline</span></div>
              <div><span className="ck">from</span><span className="cfn"> arhat.core </span><span className="ck">import</span><span className="ct"> Pipeline</span></div>
              <div>&nbsp;</div>
              <div><span className="ck">async def</span><span className="cfn"> process_invoice</span><span className="cw">(pdf):</span></div>
              <div><span className="cw">{"  text = "}</span><span className="ck">await</span><span className="cfn"> ocr.extract</span><span className="cw">(pdf)</span></div>
              <div>&nbsp;</div>
              <div><span className="cw">{"  result = "}</span><span className="ck">await</span><span className="cfn"> llm.parse</span><span className="cw">(text, {"{"}</span></div>
              <div><span className="cs">{"    \"vendor\""}</span><span className="cw">{": str,  "}</span><span className="cs">{"\"amount\""}</span><span className="cw">{": float,"}</span></div>
              <div><span className="cs">{"    \"date\""}</span><span className="cw">{": "}</span><span className="ct">datetime</span><span className="cw">{", "}</span><span className="cs">{"\"confidence\""}</span><span className="cw">{": "}</span><span className="cn">0.95</span></div>
              <div><span className="cw">{"  })"}</span></div>
              <div>&nbsp;</div>
              <div><span className="ck">{"  if"}</span><span className="cw">{" result.confidence > "}</span><span className="cn">0.9</span><span className="cw">:</span></div>
              <div><span className="ck">{"    await"}</span><span className="cfn"> accounting.push</span><span className="cw">(result)</span></div>
              <div><span className="ck">{"    return"}</span><span className="cs">{" \"✓ Processed\""}</span><span className="blink"/></div>
            </div>
          </div>
            {/* <div className="tech-vis rv d2">
              <div className="oring or1" />
              <div className="oring or2" />
              <div className="oring or3" />
              <div className="o-center">ARHAT<br />STACK</div>
              {CHIPS.map(({ label, style }) => (
                <div key={label} className="o-chip" style={style}>{label}</div>
              ))}
            </div> */}
          </div>
        </div>
      </section>
      <hr className="ghr" />

      {/* ── AUTO TABS ── */}
      <section className="section">
        <div className="wrap">
          <div className="s-head rv">
            <div className="s-label">📋 Services Detail</div>
            <h2 className="s-title">What We <span className="acc">Deliver</span></h2>
          </div>
          <AutoTabs items={TABS} />
  
        </div>
      </section>
      <hr className="ghr" />

      {/* OSS PREVIEW */}
      <section className="section">
        <div className="wrap">
          <div className="s-head rv">
            <div className="s-label">⚡ Open Source</div>
            <h2 className="s-title">Tools We Ship, Freely.</h2>
            <p className="s-sub">Production-ready starters so you don't reinvent the foundation.</p>
          </div>
          <div className="oss-grid">
            {[
              { ico:'🚀', mono:'django-production-starter',        title:'Django Production Starter Kit',     desc:'Production-ready Django template — REST APIs, PostgreSQL, JWT auth, Docker, GitHub Actions. Ship in hours, not days.' },
              { ico:'🧠', mono:'django-openai-knowledgebase',       title:'Django + OpenAI Knowledgebase',     desc:'Starter for AI-powered knowledgebase apps — Django admin, OpenAI query endpoint, pgvector semantic search.' },
              { ico:'📑', mono:'ai-document-processing-pipeline',   title:'AI Document Processing Pipeline',   desc:'Python pipeline for extracting structured data from documents via OCR and LLMs with confidence scoring.' },
              { ico:'🖥️', mono:'developer-vdi-setup',              title:'Remote Dev Environment Guide',      desc:'Scripts and runbook for secure virtual development environments — Ubuntu, Docker, SSH, pyenv, GitHub setup.' },
            ].map((o, i) => (
              <div key={i} className={`oss-card rv d${i+1}`}>
                <div>{o.ico}</div>
                <div className="oss-mono">{o.mono}</div>
                <h3>{o.title}</h3>
                <p>{o.desc}</p>
                <Link to="/open-source" className="oss-link">View Details →</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <div className="quote-section rv">
        <span className="qmark">"</span>
        <blockquote>
          To help businesses build <em>reliable software systems that scale</em>,
          while leveraging AI to automate real workflows — not just to demo.
        </blockquote>
        <p className="qwho">— The arhatinfo.com mission</p>
      </div>

      <CtaSection />

    </div>
  );
}
